import { useCallback, useMemo, useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
import { useHotkeys } from '@blueprintjs/core';
import { CheckIcon, PlusCircledIcon } from '@radix-ui/react-icons';
import { useShallow } from 'zustand/react/shallow';

import { trackChannelFilterButtonClicked } from '@/lib/analytics-event';
import { cn } from '@/lib/utils';

import TooltipComponent from '../common/tooltip/tooltip';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';

const ConversationChannelPicker = ({ disabled }) => {
  const { channels, selectedChannels, setSelectedChannels } = useInboxStore(
    useShallow((state) => ({
      channels: state.channels,
      selectedChannels: state.selectedChannels,
      setSelectedChannels: state.setSelectedChannels,
    }))
  );

  const addItem = (newItem) => {
    if (!disabled) {
      setSelectedChannels([...selectedChannels, newItem]);
    }
  };

  const removeItem = (itemToRemove) => {
    if (!disabled) {
      const updatedItems = selectedChannels.filter(
        (item) => item !== itemToRemove
      );
      setSelectedChannels(updatedItems);
    }
  };

  // change this
  const handleKeyPress = useCallback(() => {
    setFilterChannelsPopoverOpen(true);
    trackChannelFilterButtonClicked('keyboard');
  }, []);

  const hotkeys = useMemo(
    () => [
      {
        combo: 'shift + C',
        global: true,
        group: 'Filtering',
        // disabled: !contactSidebarShow,
        label: 'Filter Channels',
        onKeyUp: handleKeyPress,
      },
    ],
    [handleKeyPress]
  );
  const { handleKeyDown, handleKeyUp } = useHotkeys(hotkeys);

  const [filterChannelsPopoverOpen, setFilterChannelsPopoverOpen] =
    useState(false);

  return (
    <Popover
      open={filterChannelsPopoverOpen}
      onOpenChange={setFilterChannelsPopoverOpen}
    >
      <TooltipComponent
        content={
          <div className='flex space-x-2'>
            <div>Filter channel</div>
            <div className='flex space-x-1'>
              <kbd className='pointer-events-none  hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex'>
                shift
              </kbd>
              <kbd className='pointer-events-none  hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex'>
                C
              </kbd>
            </div>
          </div>
        }
      >
        <PopoverTrigger asChild onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
          <Button
            variant='outline'
            size='sm'
            className='h-8 select-none border-dashed'
            onClick={() => trackChannelFilterButtonClicked('mouse')}
            disabled={disabled}
          >
            <PlusCircledIcon className='mr-2 h-4 w-4' />
            Channel
            {selectedChannels?.length > 0 && (
              <>
                <Separator orientation='vertical' className='mx-1 h-4' />
                <Badge
                  variant='secondary'
                  className='rounded-sm px-1 font-normal'
                >
                  {selectedChannels.length}
                </Badge>
              </>
            )}
          </Button>
        </PopoverTrigger>
      </TooltipComponent>

      <PopoverContent
        className='w-[200px] p-0'
        align='start'
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {channels.length > 0 ? (
          <Command>
            <CommandInput placeholder={'Owner'} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <ScrollArea className='h-[250px]'>
                  {channels.map((option) => {
                    const isSelected = selectedChannels.find(
                      (element) => element === option._id
                    );
                    return (
                      <CommandItem
                        key={option._id}
                        onSelect={() => {
                          if (isSelected) {
                            removeItem(option._id);
                          } else {
                            addItem(option._id);
                          }
                        }}
                      >
                        <div
                          className={cn(
                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible'
                          )}
                        >
                          <CheckIcon className={cn('h-4 w-4')} />
                        </div>
                        {option.icon && (
                          <option.icon className='mr-2 h-4 w-4 text-muted-foreground' />
                        )}
                        <span>{option.name}</span>
                      </CommandItem>
                    );
                  })}
                </ScrollArea>
              </CommandGroup>
              {selectedChannels.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        if (!disabled) {
                          setSelectedChannels([]);
                        }
                      }}
                      className='justify-center text-center'
                    >
                      Clear Filter
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        ) : (
          <div className='py-6 text-center text-sm'> No Channels</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ConversationChannelPicker;
